import { createStore } from "zustand/vanilla";
import {
  subscribeWithSelector,
  devtools,
  persist,
  createJSONStorage,
} from "zustand/middleware";

import { createAppSlice } from "./appSlice";
import { createImsSlice } from "./imsSlice";
import { createStorySlice } from "./storySlice";
import { createWizardSlice } from "./wizardSlice";
import { createPromptStorySlice } from "./promptStorySlice";
import { createPromptPersonasSlice } from "./promptPersonasSlice";
import { createPromptVisualsSlice } from "./promptVisualsSlice";

export const store = createStore(
  subscribeWithSelector(
    devtools(
      persist(
        (...args) => ({
          ...createAppSlice(...args),
          ...createImsSlice(...args),
          ...createStorySlice(...args),
          ...createWizardSlice(...args),
          ...createPromptStorySlice(...args),
          ...createPromptPersonasSlice(...args),
          ...createPromptVisualsSlice(...args),
        }),
        {
          name: "exd-storyteller-store",
          storage: createJSONStorage(() => localStorage),
          version: 2,
          partialize: ({
            stories,
            wizard,
            ims,
            redirectPath,
            colorSchemePreference,
            rolesDisabled,
            devModeOverride,
            demoMode,
          }) => ({
            stories,
            wizard,
            colorSchemePreference,
            rolesDisabled,
            devModeOverride,
            demoMode,
            ims: {
              profile: ims.profile,
              token: ims.token,
              org: ims.org,
              orgTokens: ims.orgTokens,
            },
            redirectPath,
          }),
          migrate: (persistedState, version) => {
            if (version === 0) {
              // Migration logic for version 0 to 1
              persistedState.wizard.data = persistedState.data;
              persistedState.wizard.sampleData = persistedState.sampleData;
              persistedState.wizard.storyPrompt = persistedState.storyPrompt;
              persistedState.wizard.storyPromptTemplate =
                persistedState.storyPromptTemplate;

              delete persistedState.data;
              delete persistedState.sampleData;
              delete persistedState.storyPrompt;
              delete persistedState.storyPromptTemplate;
            }

            if (version === 1 || version === 2) {
              // Migration logic for version 1 to 2
              persistedState.stories = persistedState.stories.map(
                ({ id, uuid, ...story }) => ({
                  id: id ?? uuid,
                  ...story,
                }),
              );
            }

            return persistedState;
          },
        },
      ),
      {
        name: "exd-storyteller-store",
        enabled: process.env.NODE_ENV === "development",
      },
    ),
  ),
);

const storiesKeys = store.getState().stories.map((story) => story.id);
const uniqueStoriesKeys = new Set(storiesKeys);
if (storiesKeys.length !== uniqueStoriesKeys.size) {
  // Remove duplicate stories
  const uniqueStories = new Map();
  uniqueStoriesKeys.forEach((storyId) => {
    const story = store.getState().stories.find((s) => s.id === storyId);
    if (story) {
      uniqueStories.set(story.id, story);
    }
  });
  store.setState(
    () => ({
      stories: Array.from(uniqueStories.values()),
    }),
    false,
    "removeDuplicateStories",
  );
}

const IS_DARK_MODE_MATCH = window.matchMedia("(prefers-color-scheme: dark)");
const IS_MOBILE_VIEWPORT_MATCH = window.matchMedia(`(max-width: 767px)`);
const IS_LARGE_VIEWPORT_MATCH = window.matchMedia(`(min-width: 1720px)`);

store
  .getState()
  .setColorSchemePreference(store.getState().colorSchemePreference);

IS_DARK_MODE_MATCH.addEventListener("change", (event) => {
  if (store.getState().colorSchemePreference !== "system") {
    return;
  }
  const theme = event.matches ? "dark" : "light";
  store.getState().setColorScheme(theme);
});

store.getState().setIsMobile(IS_MOBILE_VIEWPORT_MATCH.matches);

IS_MOBILE_VIEWPORT_MATCH.addEventListener("change", (event) => {
  store.getState().setIsMobile(event.matches);
});

store.getState().setIsLargeViewport(IS_LARGE_VIEWPORT_MATCH.matches);

IS_LARGE_VIEWPORT_MATCH.addEventListener("change", (event) => {
  store.getState().setIsLargeViewport(event.matches);
});
